import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';
import knowledgeSvgDark from '../assets/knowledgeHubDiagram.svg';
import knowledgeSvgLight from '../assets/knowledgeHubDiagramLight.svg';
import '../styles/productPage.css';

const TABS = ['overview', 'features', 'howItWorks', 'useCases'];

const HIGHLIGHT_ICONS = {
  search: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
      <line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
    </svg>
  ),
  chat: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      <line x1="9" y1="10" x2="15" y2="10"/><line x1="9" y1="14" x2="12" y2="14"/>
    </svg>
  ),
  pattern: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
    </svg>
  ),
  automation: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  ),
};

const KnowledgeHub = () => {
  const { t } = useTranslation('knowledgeHub');
  const { language } = useLanguage();
  const { theme } = useTheme();
  const knowledgeSvg = theme === 'light' ? knowledgeSvgLight : knowledgeSvgDark;
  const [activeTab, setActiveTab] = useState(0);
  const tabsSectionRef = useRef(null);

  const goToTab = (index) => {
    setActiveTab(index);
    requestAnimationFrame(() => {
      if (tabsSectionRef.current) {
        // offsetTop is the section's absolute position; subtract sticky header height
        const top = tabsSectionRef.current.offsetTop - 105;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    });
  };

  /* safe array from locale */
  const tArr = (key) => {
    const val = t(key, { returnObjects: true });
    return Array.isArray(val) ? val : [];
  };

  return (
    <div className="product-page">
      <SEO
        title="E1 Knowledge Hub - AI Knowledge Base | Semantic Search & AI Chat"
        description="E1 Knowledge Hub turns your company documents, policies, and research into a searchable AI knowledge layer. Ask questions in plain language, get cited answers instantly, and automate knowledge workflows with AI agents."
        keywords="AI knowledge base Malaysia, semantic search enterprise, AI chat assistant documents, custom software development AI knowledge, AI solutions knowledge management, predictive AI document analysis, AI agents knowledge base, enterprise document intelligence, E1 Knowledge Hub EWANDS"
        canonicalPath={`/${language}/knowledge-hub`}
      />

      {/* Hero */}
      <section className="product-hero product-hero--knowledge-hub">
        <div className="container">
          <div className="product-hero-inner">
            <span className="product-hero-tag">{t('overview.tag')}</span>
            <h1>{t('hero.title')}</h1>
            <p className="product-hero-subtitle">{t('hero.subtitle')}</p>
            <p className="product-hero-desc">{t('hero.description')}</p>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="product-tabs-section" ref={tabsSectionRef}>
        <div className="product-tab-bar-wrapper">
          <div className="container">
            <div className="product-tab-bar">
              {TABS.map((tab, i) => (
                <button
                  key={tab}
                  className={`product-tab-btn${activeTab === i ? ' active' : ''}`}
                  onClick={() => goToTab(i)}
                >
                  {t(`tabs.${tab}`)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="product-tab-content">
          <div className="container">

            {/* ── Overview ───────────────────────────────── */}
            {activeTab === 0 && (
              <div>
                <span className="pp-section-tag">{t('overview.tag')}</span>
                <h2 className="pp-section-heading">{t('overview.heading')}</h2>
                <p className="pp-section-desc">{t('overview.description')}</p>

                {/* Enlarged highlight cards with icons */}
                <div className="pp-overview-highlights">
                  {[
                    { k: 'h1', icon: HIGHLIGHT_ICONS.search },
                    { k: 'h2', icon: HIGHLIGHT_ICONS.chat },
                    { k: 'h3', icon: HIGHLIGHT_ICONS.pattern },
                    { k: 'h4', icon: HIGHLIGHT_ICONS.automation },
                  ].map(({ k, icon }) => (
                    <div className="pp-overview-card" key={k}>
                      <div className="pp-overview-card-icon">{icon}</div>
                      <div className="pp-overview-card-label">{t(`overview.highlights.${k}.label`)}</div>
                      <div className="pp-overview-card-desc">{t(`overview.highlights.${k}.desc`)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Features ───────────────────────────────── */}
            {activeTab === 1 && (
              <div>
                <span className="pp-section-tag">{t('features.tag')}</span>
                <h2 className="pp-section-heading">{t('features.heading')}</h2>
                <div className="pp-feature-grid">
                  {['semanticSearch','fileSearch','documentIngestion','intelligentAssistant','patternDiscovery','versionControl','accessControl','analytics'].map(key => (
                    <div className="pp-feature-card" key={key}>
                      <span className="pp-feature-tag">{t(`features.items.${key}.tag`)}</span>
                      <div className="pp-feature-title">{t(`features.items.${key}.title`)}</div>
                      <div className="pp-feature-desc">{t(`features.items.${key}.description`)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── How It Works ───────────────────────────── */}
            {activeTab === 2 && (
              <div>
                <span className="pp-section-tag">{t('howItWorks.tag')}</span>
                <h2 className="pp-section-heading">{t('howItWorks.heading')}</h2>
                <p className="pp-section-desc">{t('howItWorks.description')}</p>

                <img
                  src={knowledgeSvg}
                  alt="E1 Knowledge Hub Pipeline Diagram"
                  style={{ width: '100%', borderRadius: '12px', marginTop: '1.5rem', marginBottom: '2.5rem' }}
                />

                <h3 className="pp-sub-heading">{t('howItWorks.pipeline.heading')}</h3>
                <div className="pp-arch-stages" style={{ marginTop: '1rem', marginBottom: '2.5rem' }}>
                  {['parse','chunk','embed','index'].map(key => (
                    <div className="pp-arch-stage" key={key}>
                      <span className="pp-arch-stage-step">{t(`howItWorks.pipeline.stages.${key}.step`)}</span>
                      <span className="pp-arch-stage-tag">{t(`howItWorks.pipeline.stages.${key}.tag`)}</span>
                      <div className="pp-arch-stage-title">{t(`howItWorks.pipeline.stages.${key}.title`)}</div>
                      <div className="pp-arch-stage-desc">{t(`howItWorks.pipeline.stages.${key}.description`)}</div>
                    </div>
                  ))}
                </div>

                <h3 className="pp-sub-heading">{t('howItWorks.aiLayer.heading')}</h3>
                <div className="pp-feature-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginTop: '1rem', marginBottom: '2rem' }}>
                  {['aiChat','aiInvestigation','workflowAutomation'].map(key => (
                    <div className="pp-feature-card" key={key}>
                      <span className="pp-feature-tag">{t(`howItWorks.aiLayer.items.${key}.tag`)}</span>
                      <div className="pp-feature-title">{t(`howItWorks.aiLayer.items.${key}.title`)}</div>
                      <div className="pp-feature-desc">{t(`howItWorks.aiLayer.items.${key}.description`)}</div>
                    </div>
                  ))}
                </div>

                <h3 className="pp-sub-heading">{t('howItWorks.outputs.heading')}</h3>
                <ul className="pp-bullet-list" style={{ marginTop: '0.5rem' }}>
                  {tArr('howItWorks.outputs.items').map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* ── Use Cases ──────────────────────────────── */}
            {activeTab === 3 && (
              <div>
                <span className="pp-section-tag">{t('useCases.tag')}</span>
                <h2 className="pp-section-heading">{t('useCases.heading')}</h2>
                <div className="pp-feature-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                  {['hr','legal','operations','strategy','compliance','finance'].map(key => (
                    <div className="pp-feature-card" key={key}>
                      <span className="pp-feature-tag">{t(`useCases.items.${key}.tag`)}</span>
                      <div className="pp-feature-title">{t(`useCases.items.${key}.title`)}</div>
                      <div className="pp-feature-desc">{t(`useCases.items.${key}.description`)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Tab Navigation ─────────────────────────── */}
            <div className="pp-tab-nav">
              {activeTab > 0 ? (
                <button className="pp-tab-nav-btn pp-tab-nav-prev" onClick={() => goToTab(activeTab - 1)}>
                  <span className="pp-tab-nav-dir">← Prev</span>
                  <span className="pp-tab-nav-label">{t(`tabs.${TABS[activeTab - 1]}`)}</span>
                </button>
              ) : <span />}
              {activeTab < TABS.length - 1 ? (
                <button className="pp-tab-nav-btn pp-tab-nav-next" onClick={() => goToTab(activeTab + 1)}>
                  <span className="pp-tab-nav-dir">Next →</span>
                  <span className="pp-tab-nav-label">{t(`tabs.${TABS[activeTab + 1]}`)}</span>
                </button>
              ) : <span />}
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default KnowledgeHub;
